<script setup>
  import Layout from "@/layouts/main.vue";
  import PageHeader from "@/components/page-header";
  import '@jobinsjp/vue3-datatable/dist/style.css';
  import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
  import moment from 'moment';
  import { truncate } from 'lodash';
  import { required, helpers } from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import { computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import axios from 'axios';
  import { authHeader } from './../../../helpers/authservice/auth-header';
  import Swal from 'sweetalert2';


  const store = useStore();
  const rejectReasons = computed(() => store.state.rejectReason.rejectReasons);
  const pagination = computed(() => store.state.rejectReason.pagination);
  const notification = computed(() => store.state.notification);
  const title = ref("Reject Reasons");
  const items = ref([
    {
      text: "Home",
      href: "/"
    },
    {
      text: "Settings",
    },
    {
      text: "Reject Reasons",
      active: true,
    }
  ])

  const rejectReason = ref({
    id: '',
    name: '',
    description: '',
    remarks: '',
    status: false,
    updated_at: null
  });

  const rules = {
      name: {
          required: helpers.withMessage("Name is required", required),
      },
      description: {
          required: helpers.withMessage("Description is required", required),
      },
      remarks: {
          required: helpers.withMessage("Remarks is required", required),
      }
  }

  const submitted = ref(false);

  const v$ = useVuelidate(rules, rejectReason);

  const loading = computed(() => store.state.globals.loading);

  const query = ref({
    search: ''        
  })

  const getAllRejectReasons = async (dt) => {
    await store.dispatch('rejectReason/getRejectReasons', {
      page: dt.page,
      pageSize: dt.per_page,
      sortColumn: 'updated_at',
      search: dt.search,
      orderBy: 'desc'
    });
  }

  const getRejectReasonDetails = async (id) => {
    store.dispatch('globals/loading', null, { root: true });

    try {
        const { data } = await axios.get(`/reject_reason/${id}`, {
            headers: authHeader()
        });
        rejectReason.value = data.data;
        store.dispatch('notification/clear', null, { root: true });
        store.dispatch('globals/loaded', null, { root: true });
    } catch (err) {
        console.log(err);
        store.dispatch('globals/loaded', null, { root: true });
    }
  }

  const updateRejectReason = async () => {
    try {
      submitted.value = true
      v$.value.$touch();
      if (v$.value.$invalid) return;
      await store.dispatch('rejectReason/updateRejectReason', rejectReason.value)
    } catch (err) {
      console.log(err);
    }
  }

  const deleteRejectReason = (id, name) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.value) {
          store.dispatch('rejectReason/deleteRejectReason', id);
          Swal.fire("Deleted!", `Reject reason ${name} has been successfully deleted!`, "success");
        }
      });
  }

  const formattedDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('YYYY-MM-DD h:mm A')
  }

  const formattedText = (text) => {
    if (!text) return 'N/A';
    return truncate(text, { length: 32 });
  }
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
        <b-overlay :show="loading">
          <div class="d-flex mb-4 justify-content-between">
            <div>
              <h1 class="fs-4 text">Reject Reasons</h1>
              <small>Create and edit reject reasons</small>
            </div>
            <div>
              <router-link
                    to="/settings/reject-reasons/create"
                    class="btn btn-primary right"
                  >
                  <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
                    New Reject Reason
                </router-link>
            </div>
          </div>

        <div class="row">
          <div class="col lg-12">
              <data-table
                  :rows="rejectReasons"
                  :pagination="pagination"
                  striped
                  :loading="loading"
                  :query="query"
                  filter
                  hoverable
                  sortable
                  @loadData="getAllRejectReasons"
                >

                <template #thead>
                    <table-head>Name</table-head>
                    <table-head>Description</table-head>
                    <table-head>Remarks</table-head>
                    <table-head>Date Modified</table-head>
                    <table-head>Status</table-head>
                    <table-head>Actions</table-head>
                </template>

                <template #tbody="{row}">
                    <table-body v-text="formattedText(row.name)" :title="row.name" />
                    <table-body v-text="formattedText(row.description)" :title="row.description" />
                    <table-body v-text="formattedText(row.remarks)" :title="row.remarks" />
                    <table-body v-text="formattedDate(row.updated_at)" />
                    <table-body>
                      <b-badge :variant="row.status ? 'success' : 'danger'">
                        {{ row.status ? 'Active' : 'Inactive' }}
                      </b-badge>
                    </table-body>
                    <table-body>
                      <button
                          class="btn btn-info mr-3"
                          title="Edit"
                          data-bs-toggle="modal"
                          data-bs-target="#editRejectReasonModal"
                          @click="getRejectReasonDetails(row.id)"
                        >
                        <i class="ri-pencil-line label-icon align-middle"></i>
                      </button>
                      <button
                          @click.prevent="deleteRejectReason(row.id, row.name)"
                          type="button"
                          class="btn btn-danger"
                          title="Delete"
                        >
                        <i class="mdi mdi-delete label-icon align-middle"></i>
                      </button>
                    </table-body>
                </template>

                <template #empty>
                  <TableBodyCell colspan="10">
                      No reject reasons found
                  </TableBodyCell>
              </template>
            </data-table>
          </div>
        </div>
      </b-overlay>
  </Layout>
    <!-- Varying modal content -->
  <div class="modal fade" role="dialog" id="editRejectReasonModal" tabindex="-1" aria-labelledby="varyingcontentModalLabel" aria-hidden="true">
      <div class="modal-dialog">
          <div class="modal-content">
              <b-overlay :show="loading">
                <div class="modal-header">
                    <h5 class="modal-title" id="varyingcontentModalLabel">Reject Reason Details</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                      <form>
                          <b-alert v-if="notification && notification.message"
                              show variant="success" class="fade show" role="alert">
                              {{ notification.message }}
                          </b-alert>

                          <div v-if="notification && notification.messages.length > 0">
                              <b-alert v-for="(message, index) in notification.messages" :key="index"
                                  show variant="danger" class="fade show" role="alert">
                                  {{ message }}
                              </b-alert>
                          </div>
                          <div class="mb-3">
                              <label for="reject-reason-name" class="col-form-label required">Name</label>
                              <input 
                                :class="{ 'is-invalid': submitted && v$.name.$error }"
                                type="text" class="form-control" id="reject-reason-name" v-model="rejectReason.name">
                              <div
                                  v-for="item of v$.name.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="desc-text" class="col-form-label required">Description</label>
                              <textarea 
                                :class="{ 'is-invalid': submitted && v$.description.$error }"
                                class="form-control" id="desc-text" v-model="rejectReason.description"></textarea>
                               <div
                                  v-for="item of v$.description.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="remarks-text" class="col-form-label required">Remarks</label>
                              <textarea 
                                :class="{ 'is-invalid': submitted && v$.remarks.$error }"
                                class="form-control" id="remarks-text" v-model="rejectReason.remarks"></textarea>
                               <div
                                  v-for="item of v$.remarks.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="status-select" class="form-label required">Status</label>
                               <select id="status-select" v-model="rejectReason.status" class="form-select mb-3" aria-label="Reject Reason Status Dropdown">
                                  <option value="1">Active</option>
                                  <option value="0">Inactive</option>
                              </select>
                          </div>
                      </form>
                       <div class="text-muted">
                          Last Updated: {{ formattedDate(rejectReason.updated_at) }}
                        </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-danger" data-bs-dismiss="modal">
                      <i class="mdi mdi-close-thick"></i>
                      Close
                    </button>
                    <button @click.prevent="updateRejectReason" type="button" class="btn btn-success">
                      <i class="mdi mdi-content-save"></i>
                      Update Reject Reason
                    </button>
                </div>
              </b-overlay>
          </div>
      </div>
    </div>
</template>